import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MATCHED_OFFER_PENDING,
  MATCHED_OFFER_SENT,
  MATCHED_OFFER_CONFIRMED,
  MATCHED_OFFER_REJECTED,
  MATCHED_OFFER_EXPIRED,
  MATCHED_OFFER_CANCELED,
  MATCHED_OFFER_INCOMPLETE,
  MATCHED_OFFER_UNSENT,
  MATCHED_OFFER_MISSED,
  DealStatusMeta,
} from '@tradecafe/types/core';


interface StatusConfig {
  label: string;
  class: string;
  tooltip?: string;
}

@Component({
  selector: 'tc-future-bid-status',
  template: `
     @if (statusConfig) {
        <span
            class="label text-uppercase"
            [ngClass]="statusConfig.class"
            [matTooltip]="statusConfig.tooltip || ''"
            [matTooltipDisabled]="!statusConfig.tooltip"
        >
      {{ statusConfig.label }}
    </span>
     }
  `,
  styles: [
    `
      .label {
        display: inline-block;
        padding: 0.2em 0.6em 0.3em;
        font-size: 9px;
        font-weight: 700;
        line-height: 1;
        color: #fff;
        text-align: center;
        white-space: nowrap;
        vertical-align: baseline;
        border-radius: 0.25em;
      }
      
      .label-default {
        background-color: #777;
      }

      .label-primary {
        background-color: #337ab7;
      }

      .label-success {
        background-color: #5cb85c;
      }

      .label-info {
        background-color: #5bc0de;
      }

      .label-warning {
        background-color: #f0ad4e;
      }

      .label-danger {
        background-color: #d9534f;
      }

      /* Deal Status Styles - from custom.scss */
      .deal-status-submitted {
        background-color: #c0c409;
      }

      .deal-status-confirmed {
        background-color: #53cece;
      }

      .deal-status-invoiced {
        background-color: #5969f4;
      }

      .deal-status-docsent {
        background-color: #e55dd0;
      }

      .deal-status-withcarrier {
        background-color: #d0dd46;
      }

      .deal-status-withreceiver {
        background-color: #ef8626;
      }

      .deal-status-paid {
        background-color: #7f0841;
      }

      .deal-status-closed {
        background-color: #357272;
      }
    `,
  ],
  imports: [CommonModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FutureBidStatusComponent {
  @Input() status: number | string;
  @Input() dealStatus: string;

  private readonly statusMapping: Record<number, StatusConfig> = {
    [MATCHED_OFFER_PENDING]: {
      label: 'Pending',
      class: 'label-warning',
      tooltip: 'Matched Offer is pending buyer confirmation',
    },
    [MATCHED_OFFER_SENT]: {
      label: 'Sent',
      class: 'label-info',
      tooltip: 'Matched Offer has been sent to buyer',
    },
    [MATCHED_OFFER_CONFIRMED]: {
      label: 'Confirmed',
      class: 'label-success',
      tooltip: 'Matched Offer has been confirmed by buyer',
    },
    [MATCHED_OFFER_REJECTED]: {
      label: 'Rejected',
      class: 'label-danger',
      tooltip: 'Matched Offer was rejected by buyer',
    },
    [MATCHED_OFFER_EXPIRED]: {
      label: 'Expired',
      class: 'label-default',
      tooltip: 'Matched Offer has expired',
    },
    [MATCHED_OFFER_CANCELED]: {
      label: 'Canceled',
      class: 'label-danger',
      tooltip: 'Matched Offer was canceled',
    },
    [MATCHED_OFFER_INCOMPLETE]: {
      label: 'Incomplete',
      class: 'label-warning',
      tooltip: 'Matched Offer is incomplete and requires additional information',
    },
    [MATCHED_OFFER_UNSENT]: {
      label: 'Unsent',
      class: 'label-default',
      tooltip: 'Matched Offer has not been sent yet',
    },
    [MATCHED_OFFER_MISSED]: {
      label: 'Missed',
      class: 'label-danger',
      tooltip: 'Matched Offer missed',
    },
  };

  get statusConfig(): StatusConfig {
    if (this.dealStatus) {
      const dealMeta = DealStatusMeta[this.dealStatus];
      if (dealMeta) {
        return {
          label: dealMeta.name,
          class: dealMeta.labelClass,
          tooltip: `Deal Status: ${dealMeta.name}`,
        };
      } else return null
    }

    if(this.status === undefined || this.status === null) {
      return null
    }

    const status =
      typeof this.status === 'string' ? parseInt(this.status, 10) : this.status;
    return (
      this.statusMapping[status] || {
        label: 'Unknown',
        class: 'label-default',
        tooltip: `Unknown status: ${this.status}`,
      }
    );
  }
}
